// GET /api/v0/tenants/:tenantId/projects/:projectId/document-types 用の Mock データ (Issue #447)。
//
// tenant 配下の DocumentType 全件を返す API のモック。
// `MOCK_ALL_DOCUMENT_TYPES` (required-documents.mock 側の必要書類定義) を
// ベースに、`enable` / `createdAt` / `updatedAt` を付与して DocumentType 用に変換する。
// これにより「必要書類として登録されているもの」を tenant 配下 DocumentType として反映しつつ、
// enable=false のエントリを 1 件追加して enabledOnly フィルタの動作を検証できる。

import { MOCK_ALL_DOCUMENT_TYPES as MOCK_REQUIRED_ALL } from './required-documents.mock';

import type { DocumentTypeApiResponse } from '../../features/evidence-files/document-types.models';

/** Mock DocumentType のタイムスタンプ既定値 */
const MOCK_TIMESTAMP = '2025-01-01T00:00:00Z';

/**
 * tenant 配下の DocumentType 全件（enabled 分）。
 * 既存の `MOCK_ALL_DOCUMENT_TYPES` (RequiredDocuments 用) を DocumentType 形式に変換する。
 * `MOCK_ALL_DOCUMENT_TYPES` は common (5) + journal_entry (2) のみで employment カテゴリを
 * 含まないため、api-server の fixture と揃えるべく employment カテゴリの DocumentType を
 * 別途追加する（api-server 側 `requiredDocumentsScenario` は dispatch_contract を含む
 * 3 カテゴリ網羅のため、mock でも同等の分布を提供する）。
 */
const ENABLED_DOCUMENT_TYPES_FROM_REQUIRED: DocumentTypeApiResponse[] =
  MOCK_REQUIRED_ALL.map((d) => ({
    publicId: d.publicId,
    code: d.code,
    name: d.name,
    category: d.category,
    attributionUnit: d.attributionUnit,
    displayOrder: d.displayOrder,
    enable: true,
    createdAt: MOCK_TIMESTAMP,
    updatedAt: MOCK_TIMESTAMP,
  }));

/**
 * employment カテゴリの DocumentType（RequiredDocuments 側には未収録）。
 * api-server の fixture (`dispatch_contract`) と揃えて 1 件追加する。
 */
const EMPLOYMENT_DOCUMENT_TYPE: DocumentTypeApiResponse = {
  publicId: 'doc-type-100',
  code: 'dispatch_contract',
  name: '派遣契約書',
  category: 'employment',
  attributionUnit: 'project',
  displayOrder: 100,
  enable: true,
  createdAt: MOCK_TIMESTAMP,
  updatedAt: MOCK_TIMESTAMP,
};

/**
 * attributionUnit='case' × category='employment' の DocumentType。
 * matrix の allowedType='employee' 絞り込み (仕訳審査月セル disabled) を Mock UI 上で
 * 対話的に検証するために追加 (Issue #447 Phase B レビュー反映)。
 */
const EMPLOYMENT_CASE_DOCUMENT_TYPE: DocumentTypeApiResponse = {
  publicId: 'doc-type-101',
  code: 'employment_case_review',
  name: '雇用審査ケース書類',
  category: 'employment',
  attributionUnit: 'case',
  displayOrder: 101,
  enable: true,
  createdAt: MOCK_TIMESTAMP,
  updatedAt: MOCK_TIMESTAMP,
};

/**
 * attributionUnit='case' × category='journal_entry' の DocumentType。
 * matrix の allowedType='journal_entry' 絞り込み (社員行 disabled) を Mock UI 上で
 * 対話的に検証するために追加 (Issue #447 Phase B レビュー反映)。
 */
const JOURNAL_ENTRY_CASE_DOCUMENT_TYPE: DocumentTypeApiResponse = {
  publicId: 'doc-type-102',
  code: 'journal_entry_case_review',
  name: '仕訳審査ケース書類',
  category: 'journal_entry',
  attributionUnit: 'case',
  displayOrder: 102,
  enable: true,
  createdAt: MOCK_TIMESTAMP,
  updatedAt: MOCK_TIMESTAMP,
};

/** enable=false の DocumentType（enabledOnly=false 指定時のみ返す） */
const DISABLED_DOCUMENT_TYPE: DocumentTypeApiResponse = {
  publicId: 'doc-type-999',
  code: 'disabled_extra',
  name: '無効な書類',
  category: 'common',
  attributionUnit: 'project',
  displayOrder: 999,
  enable: false,
  createdAt: MOCK_TIMESTAMP,
  updatedAt: MOCK_TIMESTAMP,
};

/**
 * DocumentType 全件（enable の値に関わらず全て）。
 * ハンドラで enabledOnly / category フィルタを適用する。
 */
export const MOCK_TENANT_DOCUMENT_TYPES: DocumentTypeApiResponse[] = [
  ...ENABLED_DOCUMENT_TYPES_FROM_REQUIRED,
  EMPLOYMENT_DOCUMENT_TYPE,
  EMPLOYMENT_CASE_DOCUMENT_TYPE,
  JOURNAL_ENTRY_CASE_DOCUMENT_TYPE,
  DISABLED_DOCUMENT_TYPE,
];

/**
 * publicId から tenant 配下 DocumentType 全件を線形検索する。
 * PATCH /evidence-files/:id の attribution 更新時、Issue #447 で attribution 選択肢が
 * 「必要書類 (requiredDocuments)」ではなく tenant 配下全 DocumentType に拡張されたため、
 * lookup 元も MOCK_TENANT_DOCUMENT_TYPES にする必要がある
 * (required-documents.mock 側の findDocumentTypeByPublicId は
 * 必要書類ハンドラ内でのみ使用)。
 */
export function findTenantDocumentTypeByPublicId(
  publicId: string,
): DocumentTypeApiResponse | undefined {
  return MOCK_TENANT_DOCUMENT_TYPES.find((doc) => doc.publicId === publicId);
}
